import { useEffect } from 'react';
import { useForm, useController } from 'react-hook-form';
import { type UseFormReturn, useFormRules } from '@doxyme/ui/hooks';
import { isPastDate } from '@doxyme/ui/utils';
import { DEFAULT_LOCALE, useIntl } from '@doxyme/locale';
import { type InviteViaTextMessageFormValues } from './types';
import { newLine } from '../useInvitationTextBox/invitationTextHelper';
import { useFilterPreviousSelectedLanguages } from '../useFilterPreviousSelectedLanguages';

export const INVITATION_FIELD_MAX_LENGTH = 159;

export const useInviteViaTextMessageForm = ({
  invitationFooterTextLength,
}: {
  invitationFooterTextLength: number;
}): UseFormReturn<InviteViaTextMessageFormValues> => {
  const { formatMessage } = useIntl();

  const filteredPreviouslySelected = useFilterPreviousSelectedLanguages();

  const { control, ...form } = useForm<InviteViaTextMessageFormValues>({
    defaultValues: {
      phoneField: '',
      languageField: filteredPreviouslySelected[0] ?? DEFAULT_LOCALE,
      dateField: null,
      timeField: null,
    },
  });
  const dateFieldValue = form.watch('dateField');

  useEffect(() => {
    // If we have a valid date, then let's ensure the set time (if any) is still valid.
    void form.trigger('timeField');
  }, [dateFieldValue]);

  const { getRules, ...validate } = useFormRules();
  const { validatePhone, validateDate, validateTime } = validate;

  const phoneNumberFriendlyName = formatMessage({
    id: 'inviteViaTextMessageForm.phone.friendlyName',
  });

  const consentFriendlyName = formatMessage({
    id: 'inviteViaTextMessageForm.invitation.consent.friendlyName',
  });

  const { field: phoneField } = useController({
    name: 'phoneField',
    control,
    rules: {
      ...getRules({
        values: { required: true },
        friendlyName: phoneNumberFriendlyName,
      }),
      validate: validatePhone,
    },
  });

  const { field: dateField } = useController({
    name: 'dateField',
    control,
    rules: {
      validate: {
        validateDate,
        validatePastDate: (date) => {
          if (date === null) {
            return true;
          }

          if (isPastDate(date)) {
            return formatMessage({ id: 'form.validation.dateInPast' });
          }

          return true;
        },
      },
    },
  });

  const { field: timeField } = useController({
    name: 'timeField',
    control,
    rules: {
      validate: {
        validateTime,
        validatePastTime: (time) => {
          if (time === null) {
            return true;
          }

          return true;
        },
      },
    },
  });

  const { field: invitationField } = useController({
    name: 'invitationField',
    rules: {
      /* The max length dynamically changes based on the footer text (room / passcode / date / time) as that all forms part of the invitation text.
         They are separated by a new line so that needs to be taken into account */
      maxLength:
        INVITATION_FIELD_MAX_LENGTH -
        invitationFooterTextLength -
        newLine.length,
    },
    control,
  });

  const { field: consentField } = useController({
    name: 'consentField',
    control,
    rules: {
      ...getRules({
        values: { required: true },
        friendlyName: consentFriendlyName,
      }),
    },
  });

  const { field: languageField } = useController({
    name: 'languageField',
    control,
  });

  return {
    form,
    fields: {
      phoneField,
      dateField,
      timeField,
      invitationField,
      consentField,
      languageField,
    },
  };
};
